import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Users, Vote, Landmark, Building, QrCode, ExternalLink, Copy, Check, Download, Ban } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Party-specific colors - official/representative colors
const PARTY_COLORS = {
  'BJP': '#FF9933',        // Saffron - BJP's official color
  'INC': '#19AAED',        // Light Blue - Congress color
  'NCP': '#00BFFF',        // Deep Sky Blue - NCP
  'SS': '#FF6600',         // Orange - Shiv Sena
  'SS-UBT': '#F97316',     // Orange variant - Shiv Sena UBT
  'BSP': '#22C55E',        // Blue - BSP
  'AIMIM': '#22C55E',      // Green - AIMIM
  'IUML': '#138808',       // Green - IUML
  'MNS': '#FF6600',        // Orange - MNS
  'NCP-SP': '#00CED1',     // Teal - NCP-SP
  'CPI': '#DC2626',        // Red - Communist
  'CPI(M)': '#B91C1C',     // Dark Red - Communist
};

const DEFAULT_COLORS = ['#6366F1', '#EC4899', '#8B5CF6', '#14B8A6', '#F59E0B', '#EF4444', '#3B82F6'];

const getPartyColor = (partyName, index) => {
  return PARTY_COLORS[partyName] || DEFAULT_COLORS[index % DEFAULT_COLORS.length];
};

const Dashboard = () => {
  const { t, language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  // Get the citizen portal URL
  const citizenPortalUrl = `${window.location.origin}/citizen`;

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(citizenPortalUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const openCitizenPortal = () => {
    window.open('/citizen', '_blank');
  };

  const downloadQRCode = () => {
    // Get the QR code SVG element
    const svg = document.getElementById('citizen-qr-code');
    if (!svg) return;

    // Create a canvas to draw the QR code with NMC branding
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Set canvas size for high-quality output
    canvas.width = 400;
    canvas.height = 500;
    
    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Header background
    ctx.fillStyle = '#003366';
    ctx.fillRect(0, 0, canvas.width, 60);
    
    // Header text
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 18px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Nagpur Municipal Corporation', canvas.width / 2, 25);
    ctx.font = '14px Arial';
    ctx.fillText('नागपूर महानगरपालिका', canvas.width / 2, 45);
    
    // Convert SVG to image
    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);
    
    const img = new Image();
    img.onload = () => {
      // Draw QR code centered
      const qrSize = 250;
      const qrX = (canvas.width - qrSize) / 2;
      const qrY = 80;
      ctx.drawImage(img, qrX, qrY, qrSize, qrSize);
      
      // Add text below QR code
      ctx.fillStyle = '#003366';
      ctx.font = 'bold 16px Arial';
      ctx.fillText('Scan for Election Results', canvas.width / 2, 360);
      ctx.font = '14px Arial';
      ctx.fillText('निवडणूक निकालांसाठी स्कॅन करा', canvas.width / 2, 385);
      
      // Add URL
      ctx.fillStyle = '#666666';
      ctx.font = '12px Arial';
      ctx.fillText(citizenPortalUrl, canvas.width / 2, 420);
      
      // Footer
      ctx.fillStyle = '#003366';
      ctx.fillRect(0, 450, canvas.width, 50);
      ctx.fillStyle = '#ffffff';
      ctx.font = '12px Arial';
      ctx.fillText('General Election 2025-26 | सार्वत्रिक निवडणूक 2025-26', canvas.width / 2, 480);
      
      // Download the canvas as PNG
      const link = document.createElement('a');
      link.download = 'NMC_Election_QR_Code.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      URL.revokeObjectURL(svgUrl);
    };
    img.src = svgUrl;
  };

  if (loading) {
    return <div className={`text-center py-12 ${language === 'mr' ? 'marathi-text' : ''}`}>{t('loading')}</div>;
  }

  // Sort chart data by seats (descending)
  const chartData = stats?.party_seats 
    ? Object.entries(stats.party_seats)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value)
    : [];

  const statsCards = [
    {
      title: t('totalVoters'),
      value: stats?.total_voters?.toLocaleString() || '0',
      icon: Users,
      color: 'text-primary',
      testId: 'total-voters-card'
    },
    {
      title: t('totalSeats'),
      value: stats?.total_seats?.toLocaleString() || '0',
      icon: Landmark,
      color: 'text-secondary',
      testId: 'total-seats-card'
    },
    {
      title: t('pollingStations'),
      value: stats?.total_polling_stations?.toLocaleString() || '0',
      icon: Building,
      color: 'text-accent',
      testId: 'polling-stations-card'
    },
    {
      title: t('avgPolling'),
      value: stats?.avg_polling_percentage ? `${stats.avg_polling_percentage}%` : '0%',
      icon: Vote,
      color: 'text-purple-600',
      testId: 'avg-polling-card'
    },
    {
      title: language === 'en' ? 'NOTA Votes' : 'नोटा मते',
      value: stats?.total_nota_votes?.toLocaleString() || '0',
      subtitle: stats?.nota_percentage ? `(${stats.nota_percentage}%)` : '',
      icon: Ban,
      color: 'text-red-500',
      testId: 'nota-votes-card'
    },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className={`text-4xl font-bold text-primary mb-2 ${language === 'mr' ? 'marathi-text' : ''}`}>
          {t('dashboard')}
        </h1>
        <p className={`text-muted-foreground ${language === 'mr' ? 'marathi-text' : ''}`}>
          {t('appTitle')}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        {statsCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} className="hover:shadow-md transition-shadow" data-testid={stat.testId}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className={`text-sm font-medium ${language === 'mr' ? 'marathi-text' : ''}`}>
                  {stat.title}
                </CardTitle>
                <Icon className={`h-5 w-5 ${stat.color}`} />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {stat.value}
                  {stat.subtitle && <span className="text-lg text-muted-foreground ml-2">{stat.subtitle}</span>}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Party Seats Chart */}
      <Card data-testid="party-seats-chart">
        <CardHeader>
          <CardTitle className={language === 'mr' ? 'marathi-text' : ''}>
            {t('seatsWon')} - {t('party')} {language === 'en' ? 'Distribution' : 'वितरण'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getPartyColor(entry.name, index)} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className={`text-center py-12 text-muted-foreground ${language === 'mr' ? 'marathi-text' : ''}`}>
              {t('noData')}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Citizen Portal QR Code */}
      <Card data-testid="citizen-portal-qr-card" className="bg-gradient-to-br from-[#003366] to-[#004d99]">
        <CardHeader>
          <CardTitle className={`text-white flex items-center gap-2 ${language === 'mr' ? 'marathi-text' : ''}`}>
            <QrCode className="h-5 w-5" />
            {language === 'en' ? 'Citizen Portal QR Code' : 'नागरिक पोर्टल QR कोड'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row items-center gap-8">
            {/* QR Code */}
            <div className="bg-white p-4 rounded-xl shadow-lg">
              <QRCodeSVG 
                id="citizen-qr-code"
                value={citizenPortalUrl}
                size={180}
                level="H"
                includeMargin={true}
                bgColor="#ffffff"
                fgColor="#003366"
              />
            </div>
            
            {/* Info & Actions */}
            <div className="flex-1 text-white space-y-4">
              <div>
                <h3 className={`text-xl font-bold mb-2 ${language === 'mr' ? 'marathi-text' : ''}`}>
                  {language === 'en' ? 'Share Election Results with Citizens' : 'नागरिकांसोबत निवडणूक निकाल शेअर करा'}
                </h3>
                <p className={`text-blue-200 ${language === 'mr' ? 'marathi-text' : ''}`}>
                  {language === 'en' 
                    ? 'Scan this QR code to access the public citizen portal with live election results. Any data updates made here will be reflected instantly.'
                    : 'लाइव्ह निवडणूक निकालांसह सार्वजनिक नागरिक पोर्टलवर प्रवेश करण्यासाठी हा QR कोड स्कॅन करा. येथे केलेले कोणतेही डेटा अपडेट त्वरित प्रतिबिंबित होतील.'}
                </p>
              </div>
              
              {/* URL Display */}
              <div className="bg-white/10 rounded-lg p-3 flex items-center justify-between gap-2">
                <code className="text-sm text-blue-100 truncate flex-1">{citizenPortalUrl}</code>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={copyToClipboard}
                  className="text-white hover:bg-white/20"
                  data-testid="copy-url-btn"
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                <Button 
                  onClick={openCitizenPortal}
                  className="bg-white text-[#003366] hover:bg-blue-100"
                  data-testid="open-citizen-portal-btn"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  {language === 'en' ? 'Open Portal' : 'पोर्टल उघडा'}
                </Button>
                <Button 
                  onClick={downloadQRCode}
                  className="bg-green-500 text-white hover:bg-green-600"
                  data-testid="download-qr-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {language === 'en' ? 'Download QR' : 'QR डाउनलोड करा'}
                </Button>
                <Button 
                  variant="outline"
                  onClick={copyToClipboard}
                  className="border-white text-white hover:bg-white/20"
                  data-testid="copy-link-btn"
                >
                  {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                  {copied 
                    ? (language === 'en' ? 'Copied!' : 'कॉपी झाले!') 
                    : (language === 'en' ? 'Copy Link' : 'लिंक कॉपी करा')}
                </Button>
              </div>
              
              {/* Sync Info */}
              <div className="flex items-center gap-2 text-sm text-green-300">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                {language === 'en' 
                  ? 'Real-time sync enabled - Changes reflect instantly on citizen portal'
                  : 'रिअल-टाइम सिंक सक्षम - बदल नागरिक पोर्टलवर त्वरित प्रतिबिंबित होतात'}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;